import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, Save, Send } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-secondary/50 p-3" data-testid={testid}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-mono-data text-xl font-semibold">{value}</div>
  </div>
);

export const PersonalCodesAdmin = () => {
  const [info, setInfo] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/personal-codes");
    setInfo(data); setCfg(data.config);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/admin/personal-codes/config", { ...cfg, value: Number(cfg.value), valid_days: Number(cfg.valid_days), after_days: Number(cfg.after_days) });
      toast.success("Impostazioni codici personali salvate"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runNow = async () => {
    setBusy("run");
    try { const { data } = await api.post("/admin/personal-codes/run"); toast.success(`Codici personali inviati: ${data.sent}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!cfg) return null;
  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });

  return (
    <Card className="rounded-2xl" data-testid="personal-codes-admin">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> Codici sconto personali</CardTitle>
        <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">Invio automatico</span>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="personal-enabled-switch" /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Ogni giorno alle 10:00 gli utenti non Premium registrati da almeno {cfg.after_days} giorni ricevono un codice monouso personale (email + notifica in-app).</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1.5"><Label>Tipo</Label>
            <Select value={cfg.discount_type} onValueChange={(v) => setCfg({ ...cfg, discount_type: v })}>
              <SelectTrigger data-testid="personal-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">Percentuale (%)</SelectItem><SelectItem value="amount">Importo (€)</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Valore</Label><Input type="number" min="0" step="0.01" value={cfg.value} onChange={set("value")} data-testid="personal-value" /></div>
          <div className="space-y-1.5"><Label>Validità (giorni)</Label><Input type="number" min="1" value={cfg.valid_days} onChange={set("valid_days")} data-testid="personal-valid-days" /></div>
          <div className="space-y-1.5"><Label>Dopo (giorni)</Label><Input type="number" min="1" value={cfg.after_days} onChange={set("after_days")} data-testid="personal-after-days" /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Codici inviati" value={info.sent} testid="personal-stat-sent" />
          <Stat label="Codici usati" value={info.used} testid="personal-stat-used" />
          <Stat label="Tasso di utilizzo" value={`${info.use_rate}%`} testid="personal-stat-rate" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={save} disabled={!!busy} data-testid="personal-save-button"><Save className="h-4 w-4 mr-2" /> {busy === "save" ? "Salvataggio…" : "Salva"}</Button>
          <Button variant="outline" onClick={runNow} disabled={!!busy} data-testid="personal-run-button"><Send className="h-4 w-4 mr-2" /> {busy === "run" ? "Invio…" : "Invia ora ai nuovi idonei"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};
